<template>
  <div>
    <VueDatePicker ref="vueDatepicker"
                   class="datepicker"
                   :class="{
                     'datepicker--lang-zh': language === 'zh',
                     'datepicker--invisible-input': invisibleInput,
                     'datepicker--day-picker': type === 'day'
                   }"
                   :model-value="datepickerValue"
                   :disabled-dates="customDisabledDates"
                   :is24="is24HourFormat"
                   :locale="language"
                   :format="format !== undefined ? format : getFormattedDateForInput"
                   :month-picker="type === 'month'"
                   :year-picker="type === 'year'"
                   :max-date="maxDateFormatted"
                   :min-date="minDateFormatted"
                   :disabled="disabled"
                   :readonly="readOnly"
                   :clearable="clearButton"
                   :state="state"
                   :enable-time-picker="withTimeSelection"
                   :auto-apply="autoApply && !withTimeSelection"
                   :position="position"
                   :year-range="yearRangeByConfig"
                   :dark="isDarkMode"
                   :range="rangeConfiguration"
                   :alt-position="customPosition"
                   :prevent-min-max-navigation="!!(maxDateFormatted || minDateFormatted)"
                   hide-offset-dates
                   @internal-model-change="internalModelChange"
                   @range-start="onRangeStart"
                   @open="focusInput"
                   @closed="onClosed"
                   @update:model-value="onChange">
      <template #dp-input="{ value, isMenuOpen }">
        <slot name="dp-input"
              v-bind="{ value, isMenuOpen }">
          <TextInputGroup ref="textInputGroup"
                          :value="value"
                          :placeholder="placeholder"
                          class="datepicker__text-input-group pointer"
                          :class="{ 'datepicker__text-input-group--focus': isMenuOpen }"
                          :readonly="true"
                          :disabled="disabled">
            <template #prependText>
              <Icon name="calendar"
                    :title="tr('common.datepicker.OPEN')"></Icon>
            </template>
          </TextInputGroup>
        </slot>
      </template>
      <template #top-extra>
        <slot name="top-extra"></slot>
      </template>
      <template #arrow-left>
        <Icon name="arrow-left3"
              :title="tr('common.datepicker.PREV')"></Icon>
      </template>
      <template #arrow-right>
        <Icon name="arrow-right3"
              :title="tr('common.datepicker.NEXT')"></Icon>
      </template>
      <template #calendar-icon>
        <Icon name="calendar"
              :title="tr('javascript.TX_BACK')"></Icon>
      </template>
      <template #clear-icon="{ clear }">
        <Icon name="cross2"
              class="custom-close pr-2"
              :title="tr('common.datepicker.CLEAR')"
              @click="clear"></Icon>
      </template>
      <template #clock-icon>
        <Icon name="clock"
              :title="tr('common.datepicker.SELECT_TIME')"
              :size="BootstrapVariant.LARGE"
              class="mr-2"></Icon>
        <span class="time-picker-label">
          {{ timePickerInfoText }}
        </span>
      </template>
      <template #action-row="{ selectDate, closePicker }">
        <SaveModalButtonGroup :size="BootstrapVariant.SMALL"
                              @save="selectDate"
                              @cancel="closePicker">
        </SaveModalButtonGroup>
      </template>
    </VueDatePicker>
  </div>
</template>

<script>
  import VueDatePicker from '@vuepic/vue-datepicker';
  import SaveModalButtonGroup from '@common/layout/SaveModalButtonGroup.vue';
  import makeMoment from '@common/moment/MomentFactory';
  import VcomProvider from '../../VcomProvider';
  import { translator } from '~/zf/frontend/common/mixins';
  import LocalizationService from '~/zf/frontend/common/localization/LocalizationService';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import MomentFormat from '~/zf/frontend/common/moment/MomentFormat';
  import Locale from '~/zf/frontend/common/localization/Locale';
  import Icon from '../layout/Icon.vue';
  import TextInputGroup from '../input/TextInputGroup.vue';
  import '@vuepic/vue-datepicker/dist/main.css';

  VueDatePicker.compatConfig = { MODE: 3 };

  export default {
    name: 'DatePicker',
    compatConfig: {
      MODE: 3,
    },
    components: {
      VueDatePicker,
      Icon,
      TextInputGroup,
      SaveModalButtonGroup,
    },
    mixins: [translator],
    props: {
      modelValue: {
        required: true,
      },
      clearButton: {
        type: Boolean,
        default: false,
      },
      /*
        attribute "custom" will be passed to the datepicker as is
        according to the docs this may be an array of dates or strings or be a function
      */
      disabledDates: {
        type: Object,
        validator(disabledDates) {
          return Object.keys(disabledDates).every((key) => ['to', 'from', 'custom'].includes(key));
        },
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
      format: {
        type: String,
      },
      type: {
        type: String,
        default: 'day',
        validator(variant) {
          return ['day', 'month', 'year'].includes(variant);
        },
      },
      placeholder: {
        type: String,
      },
      state: {
        type: Boolean,
        default: null,
      },
      autoApply: {
        type: Boolean,
        default: true,
      },
      allowFutureDates: {
        type: Boolean,
        default: true,
      },
      rangeConfiguration: {
        type: Object,
      },
      position: {
        type: String,
        default: 'left',
        validator(variant) {
          return ['left', 'center', 'right'].includes(variant);
        },
      },
      invisibleInput: {
        type: Boolean,
        default: false,
      },
      yearRange: {
        type: Array,
        default: null,
      },
      customPosition: {
        type: [Function, null],
        default: null,
      },
      withTimeSelection: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    expose: ['showCalendar', 'hideCalendar'],
    data() {
      return {
        language: '',
        MomentFormat,
        BootstrapVariant,
        firstDateOfRangeSelected: null,
        internalValue: this.modelValue,
      };
    },
    computed: {
      is24HourFormat() {
        return VcomProvider.LanguageSwitch.getCurrentLocale() !== 'en_US';
      },
      datepickerValue() {
        // when some custom format is passed by the parent keep it as is
        // this can be the case for month picker or date range picker
        if (typeof this.modelValue === 'object' || Array.isArray(this.modelValue)) {
          return this.modelValue;
        }

        // for days just keep the input. expects a valid date string
        if (this.type === 'day') {
          return this.modelValue;
        }

        const momentDate = makeMoment(this.modelValue);
        const year = momentDate.year();

        // year picker expects a plain number
        if (this.type === 'year') {
          return year;
        }

        // month picker expects this object format
        return {
          year,
          month: momentDate.month(),
        };
      },
      minMaxLimitationByRangeStart() {
        if (this.firstDateOfRangeSelected && this.rangeConfiguration && this.rangeConfiguration.maxRange) {
          const startDateMoment = makeMoment(this.firstDateOfRangeSelected);
          return {
            min: makeMoment(startDateMoment).subtract(this.rangeConfiguration.maxRange, 'days'),
            max: makeMoment(startDateMoment).add(this.rangeConfiguration.maxRange, 'days'),
          };
        }
        return null;
      },
      minDateByConfig() {
        return this.disabledDates?.to;
      },
      minDate() {
        if (this.minMaxLimitationByRangeStart) {
          if (this.minDateByConfig) {
            return makeMoment.max([makeMoment(this.minMaxLimitationByRangeStart.min), makeMoment(this.minDateByConfig)]);
          }
          return this.minMaxLimitationByRangeStart.min;
        }
        return this.minDateByConfig;
      },
      minDateFormatted() {
        if (this.minDate) {
          return makeMoment(this.minDate).toDate();
        }
        return null;
      },
      maxDateByConfig() {
        if (this.disabledDates && this.disabledDates.from) {
          return this.disabledDates.from;
        }
        if (!this.allowFutureDates) {
          return new Date();
        }
        return undefined;
      },
      maxDate() {
        if (this.minMaxLimitationByRangeStart) {
          if (this.maxDateByConfig) {
            return makeMoment.min([makeMoment(this.minMaxLimitationByRangeStart.max), makeMoment(this.maxDateByConfig)]);
          }
          return this.minMaxLimitationByRangeStart.max;
        }
        return this.maxDateByConfig;
      },
      maxDateFormatted() {
        if (this.maxDate) {
          return makeMoment(this.maxDate).toDate();
        }
        return null;
      },
      yearRangeByConfig() {
        if (this.yearRange === null) {
          if (this.minDate && makeMoment(this.minDate).year() < 2000) {
            return [makeMoment(this.minDate).year(), new Date().getFullYear() + 10];
          }
          return [2000, new Date().getFullYear() + 10];
        }
        return this.yearRange;
      },
      customDisabledDates() {
        return this.disabledDates?.custom;
      },
      isDarkMode() {
        return VcomProvider.Layout.getTheme() === 'darkify';
      },
      timePickerInfoText() {
        const timeFormat = this.MomentFormat.LOCALE_TIME;
        if (this.rangeConfiguration && this.internalValue) {
          const startMoment = makeMoment(this.internalValue[0]);
          const hasSecondDate = !!this.internalValue[1];
          const endMoment = hasSecondDate ? makeMoment(this.internalValue[1]) : makeMoment(this.modelValue[1]);
          const labelStarts = this.tr('common.datepicker.TIME_STARTS');
          const labelEnds = this.tr('common.datepicker.TIME_ENDS');
          return `${labelStarts} ${startMoment.format(timeFormat)}, ${labelEnds} ${endMoment.format(timeFormat)}`;
        }
        return makeMoment(this.internalValue).format(timeFormat);
      },
    },
    watch: {
      withTimeSelection: {
        async handler() {
          await this.$nextTick();
          this.$refs.vueDatepicker.parseModel();
        },
      },
    },
    created() {
      this.language = Locale.getLanguageCode();
    },
    methods: {
      internalModelChange(newVal) {
        this.internalValue = newVal;
      },
      onChange(value) {
        // year selection returns a number. For consistency we enforce a string here, too
        if (this.type === 'year') {
          const enforcedStringBasedDate = `${value}`;
          this.$emit('update:modelValue', enforcedStringBasedDate);
          return;
        }

        // month selection returns an object with "month" and "year"
        // range selection returns an array of date strings
        // date selection returns a date string
        // all should be kept as is
        this.$emit('update:modelValue', value);
        this.resetFirstDateOfRange();
      },
      showCalendar() {
        this.$refs.vueDatepicker.openMenu();
      },
      hideCalendar() {
        this.$refs.vueDatepicker.closeMenu();
      },
      onRangeStart(payload) {
        this.firstDateOfRangeSelected = payload;
      },
      onClosed() {
        this.resetFirstDateOfRange();
        this.focusInput();
      },
      resetFirstDateOfRange() {
        this.firstDateOfRangeSelected = null;
      },
      focusInput() {
        if (this.$refs.textInputGroup) {
          this.$refs.textInputGroup.focus();
        }
      },
      getFormattedDateForInput(inputValue) {
        const desiredFormat = this.withTimeSelection ? MomentFormat.LOCALE_SHORT_TIME : MomentFormat.LOCALE_SHORT;
        if (Array.isArray(inputValue)) {
          const from = LocalizationService.localizeDateTimeFormat(inputValue[0], desiredFormat);
          const to = LocalizationService.localizeDateTimeFormat(inputValue[1], desiredFormat);
          return `${from} - ${to}`;
        }
        return LocalizationService.localizeDateTimeFormat(inputValue, desiredFormat);
      },
    },
  };
</script>

<style lang="scss" scoped>
.datepicker {
  --dp-border-radius: 0;
  --dp-cell-border-radius: 0;
  --dp-font-family: Inter;
  --dp-menu-padding: 0;
  --dp-cell-size: 40px;
  --dp-cell-padding: 10px;
  --dp-row-margin: 0;
  --dp-overlay-height: 150px;
  --dp-overlay-col-padding: 0;

  :deep(.dp__outer_menu_wrap) {
    max-width: 282px;
  }

  :deep(.dp__inner_nav_disabled) {
    background: transparent;
  }

  :deep(.dp__month_year_wrap) {
    margin-top: 5px;
  }

  &--lang-zh {
    &:deep(.dp__calendar_header) {
      font-weight: normal;
    }
  }

  &--day-picker {
    --dp-menu-min-width: 282px;
  }

  &--invisible-input {
    .datepicker__text-input-group {
      display: none;
    }
  }

  &:deep(.dp__theme_light) {
    --dp-primary-color: #{$blue-400};
    --dp-text-color: #{$text-color};
    --dp-background-color: #{$white};
    --dp-icon-color: #{$text-color};
    --dp-hover-icon-color: #{$text-color};
    --dp-secondary-color: #{$gray-950};
  }

  &:deep(.dp__theme_dark) {
    --dp-primary-color: #{$blue-900};
    --dp-text-color: #{$gray-775};
    --dp-background-color: #{$gray-1200};
    --dp-menu-border-color: #{$gray-1900};
    --dp-hover-color: #{$gray-1110};
    --dp-hover-text-color: #{$gray-775};
    --dp-border-color: #{$gray-775};
    --dp-icon-color: #{$gray-775};
    --dp-hover-icon-color: #{$gray-775};
    --dp-secondary-color: #{$gray-960};
  }

  :deep(.dp__overlay_col) {
    height: 100%;
  }

  :deep(.dp__overlay_cell), :deep(.dp__overlay_cell_active) {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :deep(.dp__action_row ) {
    justify-content: flex-end;
  }

  .datepicker__text-input-group {
    &--focus {
      :deep(.form-control) {
        &[readonly] {
          border-color: $blue-350;
        }
      }
    }
  }

  &.dp__theme_light {
    .datepicker__text-input-group {
      :deep(.form-control) {
        &[readonly] {
          background-color: unset;
        }
      }
    }

    :deep(.dp__inc_dec_button):hover {
      color: $gray-1100;
    }
  }

  &.dp__theme_dark {
    :deep(.form-control[readonly]) {
      background-color: $gray-1900;
      color: $gray-775;
    }

    :deep(.dp__inc_dec_button):hover {
      color: $gray-775;
    }
  }
}

.time-picker-label {
  font-size: 13px;
}

.pointer {
  cursor: pointer;
}

:deep(.dp--clear-btn) {
  z-index: 4;
}
</style>
